// Pure helpers for generic boards (the Monday shape): grouping rows, projecting
// a status column into a kanban, rendering a cell as text, searching. No DOM,
// no I/O, so every rule here has a plain test.

export const COLUMN_TYPES = [
  { value: "text", label: "Text" },
  { value: "status", label: "Status" },
  { value: "person", label: "Persoană" },
  { value: "date", label: "Dată" },
  { value: "number", label: "Număr" },
  { value: "checkbox", label: "Bifă" },
  { value: "link", label: "Link" },
];

export const TEMPLATES = [
  { value: "projects", label: "Proiecte", hint: "Status, responsabil, start, termen, progres, note" },
  { value: "tasks", label: "Sarcini", hint: "Status, responsabil, termen, prioritate" },
  { value: "blank", label: "Gol", hint: "Doar un status; adaugi ce vrei" },
];

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0) || String(a.created_at).localeCompare(String(b.created_at));

/** items -> [{ group, items }] in the board's group order; orphans go to the first group. */
export function groupItems(board, items) {
  const groups = board?.groups || [];
  if (!groups.length) return [];
  const known = new Set(groups.map((g) => g.id));
  const buckets = new Map(groups.map((g) => [g.id, []]));
  for (const it of items) {
    const gid = known.has(it.group_id) ? it.group_id : groups[0].id;
    buckets.get(gid).push(it);
  }
  return groups.map((g) => ({ group: g, items: buckets.get(g.id).sort(byOrder) }));
}

/** A status column as kanban columns: one per option, plus one for rows without a value. */
export function kanbanFor(items, column) {
  if (!column || column.type !== "status") return { columns: [], cells: {} };
  const columns = [...column.options.map((o) => ({ id: o.id, label: o.label, color: o.color })),
                   { id: "", label: "Fără status", color: "#c4c4c4" }];
  const cells = Object.fromEntries(columns.map((c) => [c.id, []]));
  for (const it of items) {
    const v = it.values?.[column.id];
    (cells[v && v in cells ? v : ""]).push(it);
  }
  for (const k of Object.keys(cells)) cells[k].sort(byOrder);
  return { columns, cells };
}

export function optionOf(column, value) {
  return column?.options?.find((o) => o.id === value) || null;
}

/** A cell as the text a person would read -- for search and for CSV. */
export function cellText(column, value, users = []) {
  if (value === null || value === undefined || value === "") return "";
  switch (column.type) {
    case "status": return optionOf(column, value)?.label || "";
    case "person": return users.find((u) => u.id === value)?.name || "";
    case "checkbox": return value ? "da" : "nu";
    case "link": return typeof value === "object" ? (value.label || value.url || "") : String(value);
    case "number": return String(value);
    default: return String(value);
  }
}

export function searchItems(board, items, q, users = []) {
  const needle = (q || "").trim().toLowerCase();
  if (!needle) return items;
  return items.filter((it) =>
    it.title.toLowerCase().includes(needle)
    || (board.columns || []).some((c) => cellText(c, it.values?.[c.id], users).toLowerCase().includes(needle)));
}

/** Items -> CSV, one row per item, the board's columns in order. */
export function toCsv(board, items, users = []) {
  const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
  const head = ["Grup", "Element", ...board.columns.map((c) => c.label)];
  const groups = new Map((board.groups || []).map((g) => [g.id, g.label]));
  const rows = items.map((it) => [
    groups.get(it.group_id) || "", it.title,
    ...board.columns.map((c) => cellText(c, it.values?.[c.id], users)),
  ]);
  return [head, ...rows].map((r) => r.map(esc).join(",")).join("\n");
}

export const canEditSchema = (board, user, role) =>
  !!board && (role === "owner" || role === "admin" || board.created_by === user?.id);

/** Progress of a group: done = last status option, on the first status column. */
export function groupProgress(board, items) {
  const st = (board.columns || []).find((c) => c.type === "status");
  if (!st || !items.length) return null;
  const last = st.options[st.options.length - 1]?.id;
  const done = items.filter((it) => it.values?.[st.id] === last).length;
  return { done, total: items.length, pct: Math.round((done / items.length) * 100) };
}
